import { avatarArt, AVATAR_EXPRESSIONS, type AvatarExpression } from "./avatarManifest.js";
import "./avatar.css";

export { AVATAR_EXPRESSIONS } from "./avatarManifest.js";

export const AVATAR_COUNT = 24;
export const AVATAR_IDS = Array.from(
  { length: AVATAR_COUNT },
  (_, i) => `avatar-${String(i + 1).padStart(2, "0")}`,
);

export function avatarManifestIsComplete(): boolean {
  return AVATAR_IDS.every((id) => AVATAR_EXPRESSIONS.every((expression) => Boolean(avatarArt(id, expression))));
}

export type { AvatarExpression } from "./avatarManifest.js";

export type AvatarTeam = 0 | 1;
export type AvatarConnection = "offline" | "autoPlaying";
export type AvatarReaction = Exclude<AvatarExpression, "idle" | "thinking">;

export interface AvatarProps {
  avatarId: string;
  size?: number;
  /** Public game-state reaction; omitted callers retain the idle portrait. */
  expression?: AvatarExpression;
  /** Stable team identity, rendered as the thin base ring on a live table. */
  team?: AvatarTeam;
  /** Marks a turn when there is no countdown overlay. */
  turn?: boolean;
  /** A small persistent presence marker, only for exceptional states. */
  connection?: AvatarConnection;
  /** Short-lived event feedback, rendered as an animated outer ring. */
  reaction?: AvatarReaction;
  className?: string;
  title?: string;
}

/**
 * Runtime avatar renderer backed by the generated manifest. The 256 px WebP
 * files already contain their shared purple circle. The outer layers carry
 * table information: team is stable, turn/reaction are transient, and
 * connection is a small exception marker rather than another full ring.
 */
export function Avatar({
  avatarId,
  size = 48,
  expression = "idle",
  team,
  turn = false,
  connection,
  reaction,
  className,
  title,
}: AvatarProps) {
  const classes = [
    "avatar",
    team === undefined ? "" : `avatar-team-${team}`,
    turn ? "avatar-turn" : "",
    connection === undefined ? "" : `avatar-connection-${connection}`,
    reaction === undefined ? "" : `avatar-reaction-${reaction}`,
    className ?? "",
  ].filter(Boolean).join(" ");

  return (
    <span
      className={classes}
      style={{ width: size, height: size }}
      role="img"
      aria-label={title ?? "Player avatar"}
    >
      <span className="avatar-art">
        <img
          src={avatarArt(avatarId, expression)}
          alt=""
          width={size}
          height={size}
          draggable={false}
        />
      </span>
    </span>
  );
}
